import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import { Message, Icon, Button, Confirm, Modal } from 'semantic-ui-react';
import styled from 'styled-components';
import NewAssessmentDetails from '../PerformanceReviews/NewAssessmentDetails';
import LiveMeetingQABreakdown from './LiveMeetingQABreakdown';
import MeetingStatusBadge, { getStatusBannerCopy } from './MeetingStatusBadge';
import TranscriptSection from './TranscriptSection';
import { DownloadReportButton, ShareSummaryButton } from './DownloadReportSection';
import theme from '../../pagesv2/theme';
import { positiveRows, improvementRows } from '../../utils/reportColumns';
import { groupAlignmentMatches } from '../MurphyReport/groupAlignmentMatches';
import {
  CardBody, StatusBanner, Section, SectionTitle, SectionContent,
  InfoGrid, InfoField, InfoLabel, InfoValue,
  ParticipantList, ParticipantChip, ListBlock, Placeholder,
  AlignmentSection, AlignmentHeader, AlignmentTitle, FitLevelBadge, AlignmentSummary,
  MatchCard, MatchProblem, QuoteBlock, QuoteAttribution, TagRow, SmallTag,
  AlignmentEmptyState,
  ProspectProfileGrid, ProfileColumn, ProfileGroup, ProfileGroupLabel,
  FitFooter, FitFooterRow
} from '../../pages/MyMeetingsPage.styles';

// ── Sub-renderers for the rich analysis shapes the developer's analyzer produces ──

const ItemCard = styled.div`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${theme.colors.background};
  border-left: 3px solid ${p => p.$accent || theme.colors.primary};
  border-radius: ${theme.radii.sm};
  margin-bottom: ${theme.spacing.sm};
  &:last-child { margin-bottom: 0; }
`;

const ItemHead = styled.div`
  font-size: ${theme.typography.body.size};
  color: ${theme.colors.text};
  font-weight: 500;
  line-height: 1.5;
`;

const ItemSub = styled.div`
  font-size: ${theme.typography.small.size};
  color: ${theme.colors.textSecondary};
  margin-top: 4px;
  line-height: 1.5;
  & > strong { color: ${theme.colors.text}; }
`;

const ItemTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
`;

const Tag = styled.span`
  display: inline-block;
  font-size: ${theme.typography.tiny.size};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 8px;
  border-radius: ${theme.radii.full};
  background: ${p => p.$bg || theme.colors.borderLight};
  color: ${p => p.$color || theme.colors.textSecondary};
  font-weight: 600;
`;

const SubColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.md};
  ${theme.media.tablet} { grid-template-columns: 1fr; }
`;

// Mirror of Assessment.positivesView / areasForImprovementView (MobX computed
// views in ui/src/models/Assessment.jsx). NewAssessmentDetails reads these
// fields directly on the assessment, but our API returns a plain JSON object
// without the model views, so we compute them here — through the SAME shared
// column util the model uses, so this view can't drift from the report page
// again (it had: no-opportunity rows hidden there still rendered red here).
const splitOverviewString = (s) => {
  if (!s || typeof s !== 'string') return [];
  return s.split('- ').slice(1).map(x => x.trim()).filter(Boolean);
};

const computePositives = (a) => {
  if (Array.isArray(a.pointsDescriptions) && a.pointsDescriptions.length > 0) {
    return positiveRows(a.pointsDescriptions).map(p => p.description);
  }
  return splitOverviewString(a.companyOverviewPositives);
};

const computeAreasForImprovement = (a) => {
  if (Array.isArray(a.pointsDescriptions) && a.pointsDescriptions.length > 0) {
    return improvementRows(a.pointsDescriptions).map(p => p.description);
  }
  return splitOverviewString(a.companyOverviewAreasForImprovement);
};

const withDerivedViews = (assessment) => ({
  ...assessment,
  positivesView: computePositives(assessment),
  areasForImprovementView: computeAreasForImprovement(assessment),
});

const renderText = (v) => {
  if (v == null) return null;
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  if (typeof v === 'object') return v.description || v.text || v.title || JSON.stringify(v);
  return String(v);
};

const severityColor = (sev) => {
  const s = (sev || '').toLowerCase();
  if (s === 'high' || s === 'critical') return { bg: theme.colors.dangerLight, color: theme.colors.danger };
  if (s === 'medium') return { bg: theme.colors.warningLight, color: theme.colors.warning };
  if (s === 'low') return { bg: theme.colors.successLight, color: theme.colors.success };
  return null;
};

const priorityColor = (pri) => {
  const p = (pri || '').toLowerCase();
  if (p === 'high' || p === 'urgent') return { bg: theme.colors.dangerLight, color: theme.colors.danger };
  if (p === 'medium') return { bg: theme.colors.warningLight, color: theme.colors.warning };
  if (p === 'low') return { bg: theme.colors.borderLight, color: theme.colors.textSecondary };
  return null;
};

const sentimentColor = (sent) => {
  const s = (sent || '').toLowerCase();
  if (s === 'positive') return { bg: theme.colors.successLight, color: theme.colors.success };
  if (s === 'negative') return { bg: theme.colors.dangerLight, color: theme.colors.danger };
  if (s === 'neutral' || s === 'mixed') return { bg: theme.colors.warningLight, color: theme.colors.warning };
  return null;
};

// ── Problem Alignment renderer ────────────────────────────────────────────────

const FIT_LEVEL_STYLES = {
  strong:   { label: 'Strong fit',      bg: theme.colors.successLight, color: theme.colors.success, border: theme.colors.success },
  moderate: { label: 'Moderate fit',    bg: theme.colors.warningLight, color: theme.colors.warning, border: theme.colors.warning },
  weak:     { label: 'Weak fit',        bg: theme.colors.dangerLight,  color: theme.colors.danger,  border: theme.colors.danger },
  none:     { label: 'No matches',      bg: theme.colors.borderLight,  color: theme.colors.textSecondary, border: theme.colors.border },
};

const EVIDENCE_STYLES = {
  explicit: { bg: theme.colors.successLight, color: theme.colors.success },
  implied:  { bg: theme.colors.warningLight, color: theme.colors.warning },
  possible: { bg: theme.colors.borderLight,  color: theme.colors.textSecondary },
};

export const ProblemAlignmentSection = ({ alignment }) => {
  const fitLevel = (alignment.fitLevel || 'none').toLowerCase();
  const fitStyles = FIT_LEVEL_STYLES[fitLevel] || FIT_LEVEL_STYLES.none;
  const matches = Array.isArray(alignment.matches) ? alignment.matches : [];

  return (
    <AlignmentSection>
      <AlignmentHeader>
        <AlignmentTitle>Problem Alignment</AlignmentTitle>
        <FitLevelBadge $bg={fitStyles.bg} $color={fitStyles.color} $border={fitStyles.border}>
          {fitStyles.label}
        </FitLevelBadge>
      </AlignmentHeader>

      {alignment.summary && <AlignmentSummary>{alignment.summary}</AlignmentSummary>}

      {matches.length === 0 ? (
        <AlignmentEmptyState>
          Murphy didn't hear any of your addressable problems mentioned in this conversation.
          Consider asking discovery questions about the problems you solve to surface fit signals.
        </AlignmentEmptyState>
      ) : (
        // One card per problem, its quotes underneath. Per-match next steps
        // are not shown here: the report's Next Steps section carries them.
        groupAlignmentMatches(matches).map((g, i) => (
          <MatchCard key={i} data-testid="alignment-problem">
            <MatchProblem>{g.problem}</MatchProblem>
            {g.quotes.map((q, qi) => {
              const evidence = EVIDENCE_STYLES[q.evidence];
              return (
                <QuoteBlock key={qi}>
                  &ldquo;{q.text}&rdquo;
                  {(q.speaker || q.evidence) && (
                    <QuoteAttribution>
                      {q.speaker ? `— ${q.speaker}` : ''}
                      {q.evidence && (
                        <SmallTag $bg={evidence?.bg} $color={evidence?.color} style={{ marginLeft: q.speaker ? 10 : 0 }}>
                          {q.evidence}
                        </SmallTag>
                      )}
                    </QuoteAttribution>
                  )}
                </QuoteBlock>
              );
            })}
            {g.offerings.length > 0 && (
              <TagRow>
                {g.offerings.map((name, oi) => (
                  <SmallTag key={oi} $bg={theme.colors.primaryGhost} $color={theme.colors.primary}>
                    {name}
                  </SmallTag>
                ))}
              </TagRow>
            )}
          </MatchCard>
        ))
      )}
    </AlignmentSection>
  );
};

// ── Prospect Profile renderer ─────────────────────────────────────────────────

const TARGET_FIT_STYLES = {
  excellent: { bg: theme.colors.successLight, color: theme.colors.success },
  good:      { bg: theme.colors.successLight, color: theme.colors.success },
  moderate:  { bg: theme.colors.warningLight, color: theme.colors.warning },
  poor:      { bg: theme.colors.dangerLight,  color: theme.colors.danger },
};

const ProspectProfileSection = ({ profile }) => {
  if (!profile) return null;
  const painPoints = Array.isArray(profile.painPoints) ? profile.painPoints : [];
  const buyingSignals = Array.isArray(profile.buyingSignals) ? profile.buyingSignals : [];
  const decisionCriteria = Array.isArray(profile.decisionCriteria) ? profile.decisionCriteria : [];
  const fit = (profile.targetMarketFit || '').toLowerCase();
  const fitStyles = TARGET_FIT_STYLES[fit];
  const hasContent = painPoints.length || buyingSignals.length || decisionCriteria.length || profile.fitReasoning || profile.targetMarketFit;
  if (!hasContent) return null;

  return (
    <Section>
      <SectionTitle>Prospect Profile</SectionTitle>
      <ProspectProfileGrid>
        <ProfileColumn>
          {painPoints.length > 0 && (
            <ProfileGroup>
              <ProfileGroupLabel>Pain Points</ProfileGroupLabel>
              <ListBlock>
                {painPoints.map((p, i) => <li key={i}>{renderText(p)}</li>)}
              </ListBlock>
            </ProfileGroup>
          )}
          {decisionCriteria.length > 0 && (
            <ProfileGroup>
              <ProfileGroupLabel>Decision Criteria</ProfileGroupLabel>
              <ListBlock>
                {decisionCriteria.map((c, i) => <li key={i}>{renderText(c)}</li>)}
              </ListBlock>
            </ProfileGroup>
          )}
        </ProfileColumn>
        <ProfileColumn>
          {buyingSignals.length > 0 && (
            <ProfileGroup>
              <ProfileGroupLabel>Buying Signals</ProfileGroupLabel>
              <ListBlock>
                {buyingSignals.map((s, i) => <li key={i}>{renderText(s)}</li>)}
              </ListBlock>
            </ProfileGroup>
          )}
        </ProfileColumn>
      </ProspectProfileGrid>
      {(profile.targetMarketFit || profile.fitReasoning) && (
        <FitFooter>
          {profile.targetMarketFit && (
            <FitFooterRow>
              <ProfileGroupLabel style={{ marginBottom: 0 }}>Target Market Fit</ProfileGroupLabel>
              <SmallTag $bg={fitStyles?.bg} $color={fitStyles?.color}>
                {profile.targetMarketFit}
              </SmallTag>
            </FitFooterRow>
          )}
          {profile.fitReasoning && <div>{profile.fitReasoning}</div>}
        </FitFooter>
      )}
    </Section>
  );
};

const AnalysisSections = ({ analysis, grade, sessionType, murphyReviewSlot }) => {
  const summary = analysis.summary || {};
  const summaryText = summary.overview || summary.overallSummary || summary.summary;
  const sentTag = sentimentColor(summary.overallSentiment);
  const showWhoWeArePitchBanner = grade === 'red' && sessionType === 'Live Meeting';

  return (
    <>
      {/* Summary */}
      {summaryText && (
        <Section>
          <SectionTitle>Summary</SectionTitle>
          <SectionContent>{summaryText}</SectionContent>
          {(summary.meetingType || summary.dealStage || summary.overallSentiment) && (
            <ItemTags style={{ marginTop: theme.spacing.sm }}>
              {summary.meetingType && <Tag>{summary.meetingType}</Tag>}
              {summary.dealStage && <Tag>{summary.dealStage}</Tag>}
              {summary.overallSentiment && (
                <Tag $bg={sentTag?.bg} $color={sentTag?.color}>{summary.overallSentiment}</Tag>
              )}
            </ItemTags>
          )}
        </Section>
      )}

      {/* Red-grade coaching banner — surface when Murphy graded a real meeting
          red. The core "Who We Are" message wasn't delivered; point the rep at
          the Casual Encounter pitch they practice in Training Hub. */}
      {showWhoWeArePitchBanner && (
        <Message negative icon style={{ marginBottom: '1rem' }}>
          <Icon name="bullhorn" />
          <Message.Content>
            <Message.Header>Deliver the "Who We Are" pitch to this prospect</Message.Header>
            <p>
              Red grade — the core company message wasn't fully delivered on this call.
              Sharpen the Casual Encounter pitch in Training Hub, then bring it to your next conversation with this prospect.
            </p>
            <Link to="/record-presentation/casual_encounter">Practice the Casual Encounter →</Link>
          </Message.Content>
        </Message>
      )}

      {/* Problem Alignment — top-of-report when present.
          Fall back to legacy `painPointAlignment` field so assessments
          analyzed before the rename still render. */}
      {(analysis.problemAlignment || analysis.painPointAlignment) && (
        <ProblemAlignmentSection alignment={analysis.problemAlignment || analysis.painPointAlignment} />
      )}

      {/* Murphy review (Positives / Areas For Improvement / Q&A breakdown).
          Slotted in directly after Problem Alignment so the headline coaching
          artifact reads right under the deal-fit context, before the deal
          intelligence details below. Rendered via slot so MeetingDetail can
          own the Assessment-model derivation. */}
      {murphyReviewSlot}

      {/* Key Moments */}
      {Array.isArray(analysis.keyMoments) && analysis.keyMoments.length > 0 && (
        <Section>
          <SectionTitle>Key Moments</SectionTitle>
          {analysis.keyMoments.map((m, i) => {
            if (typeof m === 'string') return <ItemCard key={i}><ItemHead>{m}</ItemHead></ItemCard>;
            const accent = (m.type || '').toLowerCase().includes('risk') ? theme.colors.danger
              : (m.type || '').toLowerCase().includes('opportunity') ? theme.colors.success
              : theme.colors.primary;
            return (
              <ItemCard key={i} $accent={accent}>
                <ItemHead>{m.description || renderText(m)}</ItemHead>
                {m.significance && <ItemSub><strong>Why it matters:</strong> {m.significance}</ItemSub>}
                {(m.type || m.timestamp) && (
                  <ItemTags>
                    {m.type && <Tag>{m.type}</Tag>}
                    {m.timestamp && <Tag>{m.timestamp}</Tag>}
                  </ItemTags>
                )}
              </ItemCard>
            );
          })}
        </Section>
      )}

      {/* Opportunities */}
      {Array.isArray(analysis.opportunities) && analysis.opportunities.length > 0 && (
        <Section>
          <SectionTitle>Opportunities</SectionTitle>
          {analysis.opportunities.map((o, i) => {
            if (typeof o === 'string') return <ItemCard key={i} $accent={theme.colors.success}><ItemHead>{o}</ItemHead></ItemCard>;
            return (
              <ItemCard key={i} $accent={theme.colors.success}>
                <ItemHead>{o.description || renderText(o)}</ItemHead>
                {o.recommendation && <ItemSub><strong>Recommendation:</strong> {o.recommendation}</ItemSub>}
                {o.companyAlignment && <ItemSub><strong>Why we fit:</strong> {o.companyAlignment}</ItemSub>}
                {o.type && <ItemTags><Tag $bg={theme.colors.successLight} $color={theme.colors.success}>{o.type}</Tag></ItemTags>}
              </ItemCard>
            );
          })}
        </Section>
      )}

      {/* Risks */}
      {Array.isArray(analysis.risks) && analysis.risks.length > 0 && (
        <Section>
          <SectionTitle>Risks</SectionTitle>
          {analysis.risks.map((r, i) => {
            if (typeof r === 'string') return <ItemCard key={i} $accent={theme.colors.danger}><ItemHead>{r}</ItemHead></ItemCard>;
            const sev = severityColor(r.severity);
            return (
              <ItemCard key={i} $accent={theme.colors.danger}>
                <ItemHead>{r.description || renderText(r)}</ItemHead>
                {r.mitigation && <ItemSub><strong>Mitigation:</strong> {r.mitigation}</ItemSub>}
                {(r.type || r.severity) && (
                  <ItemTags>
                    {r.type && <Tag>{r.type}</Tag>}
                    {r.severity && <Tag $bg={sev?.bg} $color={sev?.color}>{r.severity} severity</Tag>}
                  </ItemTags>
                )}
              </ItemCard>
            );
          })}
        </Section>
      )}

      {/* Next Steps */}
      {Array.isArray(analysis.nextSteps) && analysis.nextSteps.length > 0 && (
        <Section>
          <SectionTitle>Next Steps</SectionTitle>
          {analysis.nextSteps.map((s, i) => {
            if (typeof s === 'string') return <ItemCard key={i}><ItemHead>{s}</ItemHead></ItemCard>;
            const pri = priorityColor(s.priority);
            return (
              <ItemCard key={i} $accent={theme.colors.primary}>
                <ItemHead>{s.action || renderText(s)}</ItemHead>
                {(s.owner || s.timeline) && (
                  <ItemSub>
                    {s.owner && <><strong>Owner:</strong> {s.owner}</>}
                    {s.owner && s.timeline && ' · '}
                    {s.timeline && <><strong>When:</strong> {s.timeline}</>}
                  </ItemSub>
                )}
                {s.priority && (
                  <ItemTags>
                    <Tag $bg={pri?.bg} $color={pri?.color}>{s.priority} priority</Tag>
                  </ItemTags>
                )}
              </ItemCard>
            );
          })}
        </Section>
      )}

      {/* Prospect Profile — render existing-but-orphaned prospectProfile data */}
      {analysis.prospectProfile && (
        <ProspectProfileSection profile={analysis.prospectProfile} />
      )}
    </>
  );
};

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  } catch { return iso; }
};

// ── Needs-assignment panel (company-facing triage) ───────────────────────────
// Shown when /my-meetings flags the meeting needsAssignment: Murphy identified
// several licensed users on the call and couldn't tell who ran it. Candidates
// come pre-hydrated from the API; "someone else" falls back to the roster.

const AssignPanel = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.sm};
`;

const AssignSelect = styled.select`
  padding: 8px 10px;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.sm};
  font-size: ${theme.typography.body.size};
  background: ${theme.colors.surface};
  color: ${theme.colors.text};
  min-width: 260px;
`;

const AssignError = styled.div`
  width: 100%;
  color: ${theme.colors.danger};
  font-size: ${theme.typography.small.size};
`;

const AssignmentBanner = observer(({ meeting, myMeetingsStore, companyStore, usersStore }) => {
  const candidates = meeting.assignmentCandidates || [];
  const [selectedUserId, setSelectedUserId] = useState(candidates.length === 1 ? candidates[0].userId : '');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const currentUser = usersStore?.currentUser;
  const canAssign = !!currentUser &&
    currentUser.userType !== 'Sales Member' &&
    !currentUser.isReadOnly;

  const candidateIds = new Set(candidates.map(c => c.userId));
  const otherUsers = (usersStore?.companyUsers || [])
    .filter(u => !candidateIds.has(u.userId) && isPickableUser(u, currentUser))
    .map(u => ({
      userId: u.userId,
      name: [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.email
    }));

  const allOptions = [...candidates, ...otherUsers];
  const selectedName = allOptions.find(o => o.userId === selectedUserId)?.name || 'this user';
  const companyId = companyStore?.company?.id;

  const onAssign = async () => {
    setConfirmOpen(false);
    setBusy(true);
    setError(null);
    try {
      await myMeetingsStore.assignMeeting(companyId, meeting.botId, selectedUserId);
    } catch (err) {
      const apiError = err?.response?.data?.error;
      setError(apiError || 'Failed to assign the meeting. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Message warning icon>
      <Icon name="user plus" />
      <Message.Content>
        <Message.Header>Who ran this meeting?</Message.Header>
        <p style={{ marginBottom: 0 }}>
          Murphy found several licensed teammates on this call and couldn't tell who led it.
          {canAssign
            ? ' Pick the right person — Murphy will then grade the meeting and deliver their report.'
            : ' A manager with edit access can assign it so Murphy can grade it.'}
        </p>
        {canAssign && (
          <AssignPanel>
            <AssignSelect
              value={selectedUserId}
              onChange={e => setSelectedUserId(e.target.value)}
              disabled={busy}
              aria-label="Assign meeting to"
            >
              <option value="" disabled>Select a teammate…</option>
              {candidates.length > 0 && (
                <optgroup label="On this call">
                  {candidates.map(c => (
                    <option key={c.userId} value={c.userId}>{c.name}</option>
                  ))}
                </optgroup>
              )}
              {otherUsers.length > 0 && (
                <optgroup label="Someone else">
                  {otherUsers.map(u => (
                    <option key={u.userId} value={u.userId}>{u.name}</option>
                  ))}
                </optgroup>
              )}
            </AssignSelect>
            <Button
              primary
              size="small"
              disabled={!selectedUserId || busy}
              loading={busy}
              onClick={() => setConfirmOpen(true)}
            >
              Assign
            </Button>
            {error && <AssignError>{error}</AssignError>}
            <Confirm
              open={confirmOpen}
              header="Assign this meeting?"
              content={`Murphy will grade the meeting for ${selectedName} and deliver the report to them. This can be changed later.`}
              confirmButton="Assign and grade"
              cancelButton="Cancel"
              onCancel={() => setConfirmOpen(false)}
              onConfirm={onAssign}
              size="tiny"
            />
          </AssignPanel>
        )}
      </Message.Content>
    </Message>
  );
});

const formatDuration = (seconds) => {
  if (!seconds || seconds < 0) return null;
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

// ── Reassign / Share controls (managers with edit access) ────────────────────
// Server enforces the full matrix (role, isReadOnly, tree scoping, company
// membership); these controls mirror it client-side for a clean UX.

// Mirror of REASSIGN_BLOCKED_STATUSES in backend/murphy/meetings/assign.js.
const REASSIGN_BLOCKED_STATUSES = new Set([
  'downloading', 'transcribing', 'transcription_completed', 'identifying', 'analyzing'
]);

const userDisplayName = (u) =>
  [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.email || u.userId;

// Platform-admin accounts (Multiply staff) stay hidden from CUSTOMER-facing
// assign/share pickers — same convention as UsersStore.assessmentUsers — but
// an admin viewer sees the full roster, admin accounts included (internal
// companies like Multiply + Murphy legitimately share/assign between them).
const isPickableUser = (u, currentUser) =>
  u.userType !== 'admin' || currentUser?.userType === 'admin';

const ManageMeetingControls = observer(({ meeting, myMeetingsStore, companyStore, usersStore }) => {
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignTarget, setReassignTarget] = useState('');
  const [shareOpen, setShareOpen] = useState(false);
  const [shareSelection, setShareSelection] = useState(() => new Set(meeting.sharedWith || []));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const companyId = companyStore?.company?.id;
  const isDelivered = meeting.status === 'delivered';
  const reassignBlocked = REASSIGN_BLOCKED_STATUSES.has(meeting.status);

  const roster = (usersStore?.companyUsers || [])
    .filter(u => isPickableUser(u, usersStore?.currentUser));
  const reassignOptions = roster.filter(u => u.userId !== meeting.salesPersonId);
  const shareOptions = roster.filter(u => u.userId !== meeting.salesPersonId);
  const targetName = reassignOptions.find(u => u.userId === reassignTarget);

  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setReassignOpen(false);
      setShareOpen(false);
    } catch (err) {
      setError(err?.response?.data?.error || 'The change could not be saved. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const toggleShare = (userId) => {
    setShareSelection(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  return (
    <>
      <Button
        basic
        size="small"
        disabled={reassignBlocked}
        title={reassignBlocked ? 'Available once the report is delivered' : undefined}
        onClick={() => { setReassignTarget(''); setError(null); setReassignOpen(true); }}
      >
        Reassign
      </Button>
      <Button
        basic
        size="small"
        onClick={() => { setShareSelection(new Set(meeting.sharedWith || [])); setError(null); setShareOpen(true); }}
      >
        Share with teammates
      </Button>

      <Modal open={reassignOpen} onClose={() => setReassignOpen(false)} size="tiny">
        <Modal.Header>Reassign this meeting</Modal.Header>
        <Modal.Content>
          <p>
            {isDelivered
              ? 'The existing report will move to the selected user — it counts toward their stats and leaves the current owner\'s. No re-analysis; no new emails.'
              : 'The meeting (and its report, once ready) will belong to the selected user.'}
          </p>
          <AssignSelect
            value={reassignTarget}
            onChange={e => setReassignTarget(e.target.value)}
            disabled={busy}
            aria-label="Reassign meeting to"
          >
            <option value="" disabled>Select a teammate…</option>
            {reassignOptions.map(u => (
              <option key={u.userId} value={u.userId}>{userDisplayName(u)}</option>
            ))}
          </AssignSelect>
          {error && <AssignError style={{ marginTop: 8 }}>{error}</AssignError>}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setReassignOpen(false)} disabled={busy}>Cancel</Button>
          <Button
            primary
            disabled={!reassignTarget || busy}
            loading={busy}
            onClick={() => run(() => myMeetingsStore.assignMeeting(companyId, meeting.botId, reassignTarget))}
          >
            {targetName ? `Reassign to ${userDisplayName(targetName)}` : 'Reassign'}
          </Button>
        </Modal.Actions>
      </Modal>

      <Modal open={shareOpen} onClose={() => setShareOpen(false)} size="tiny">
        <Modal.Header>Share with teammates</Modal.Header>
        <Modal.Content>
          <p>
            Selected teammates can see this meeting and its report in their Live Meetings
            list. Ownership, grading, and stats stay with the assigned rep.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 260, overflowY: 'auto' }}>
            {shareOptions.map(u => (
              <label key={u.userId} style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={shareSelection.has(u.userId)}
                  disabled={busy}
                  onChange={() => toggleShare(u.userId)}
                />
                {userDisplayName(u)}
              </label>
            ))}
            {shareOptions.length === 0 && <span>No other teammates to share with.</span>}
          </div>
          {error && <AssignError style={{ marginTop: 8 }}>{error}</AssignError>}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShareOpen(false)} disabled={busy}>Cancel</Button>
          <Button
            primary
            disabled={busy}
            loading={busy}
            onClick={() => run(() => myMeetingsStore.updateSharing(companyId, meeting.botId, [...shareSelection]))}
          >
            Save sharing
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
});

const MeetingDetail = observer(({
  meeting,
  myMeetingsStore,
  companyStore,
  usersStore,
  scope
}) => {
  const banner = getStatusBannerCopy(meeting.status);
  const isFailed = meeting.status === 'failed';
  const isDelivered = meeting.status === 'delivered';
  const assessment = meeting.assessment;
  const isSharedWithMe = meeting.isSharedWithMe === true;

  // Reassign/Share are for non-read-only manager tiers on meetings that have
  // an owner. Being IN the share list must never demote a manager: a manager
  // who shares a meeting with themselves would otherwise lose the controls
  // with no UI path to undo it (the Share button itself disappears). The
  // read-only shared-viewer presentation applies only to users whose ONLY
  // access is the share. Server re-enforces the real matrix.
  const detailUser = usersStore?.currentUser;
  const canManageMeeting = !!detailUser &&
    detailUser.userType !== 'Sales Member' &&
    !detailUser.isReadOnly &&
    !meeting.needsAssignment &&
    !!meeting.salesPersonId;

  const ownerName = meeting.salesPersonName ||
    (usersStore?.companyUsers || [])
      .filter(u => u.userId === meeting.salesPersonId)
      .map(u => [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.email)[0] ||
    null;

  const onSaveToLibrary = async (questionsToSave) => {
    if (!companyStore?.company) return;
    const existing = (companyStore.company.questionsAndAnswers || []).map(qa => ({
      question: qa.question,
      answer: qa.answer,
      related_to: qa.related_to,
      ...(qa.uploadKey ? { uploadKey: qa.uploadKey } : {})
    }));
    const newQuestions = questionsToSave.filter(q =>
      !existing.some(eq => eq.question?.trim() === q.question?.trim())
    );
    if (newQuestions.length === 0) return;
    await companyStore.company.appendQuestionsAndAnswers(newQuestions);
  };

  // Managers/admins/executives can add questions from any meeting they can
  // view — including team-scope meetings, not just their own. The role gates
  // below already exclude Sales Members and any read-only role.
  const canEditLibrary = !!usersStore?.currentUser && (
    !usersStore.currentUser.isSalesMember &&
    !usersStore.currentUser.isReadOnlyExecutive &&
    !usersStore.currentUser.isReadOnlySalesManager &&
    !usersStore.currentUser.isReadOnlySeniorManager
  );

  const participants = meeting.participants && meeting.participants.length > 0
    ? meeting.participants
    : (meeting.validatedParticipants || []).map(email => ({ email, name: email, isMultiplyUser: false }));

  return (
    <CardBody>
      {/* Needs-assignment takes over from the generic status banner: the
          meeting is parked waiting on a manager, not progressing. */}
      {meeting.needsAssignment ? (
        <AssignmentBanner
          meeting={meeting}
          myMeetingsStore={myMeetingsStore}
          companyStore={companyStore}
          usersStore={usersStore}
        />
      ) : banner && (
        <StatusBanner $variant={isFailed || meeting.optedOut ? 'failed' : 'info'}>
          <strong>
            <MeetingStatusBadge status={meeting.status} />
          </strong>
          <span style={{ marginLeft: 8 }}>{banner}</span>
          {/* The opt-out record: this meeting stays listed precisely so there
              is evidence the opt-out happened and was honored. */}
          {meeting.optedOut && meeting.optOut && (
            <span style={{ marginLeft: 8, fontWeight: 600 }}>
              Requested by {meeting.optOut.requestedByName || 'a participant'}
              {meeting.optOut.at ? ` · ${formatDate(meeting.optOut.at)}` : ''}.
            </span>
          )}
        </StatusBanner>
      )}

      {/* Meeting header info — info columns left, Download report button right.
          The info fields use natural width (auto, not 1fr) so they sit tight
          and leave the right edge for the action. */}
      <Section>
        <SectionTitle>Meeting Details</SectionTitle>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 24, marginTop: 8 }}>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', flex: 1 }}>
            <InfoField>
              <InfoLabel>When</InfoLabel>
              <InfoValue>{formatDate(meeting.meetingDate) || 'TBD'}</InfoValue>
            </InfoField>
            {formatDuration(meeting.duration) && (
              <InfoField>
                <InfoLabel>Duration</InfoLabel>
                <InfoValue>{formatDuration(meeting.duration)}</InfoValue>
              </InfoField>
            )}
            {meeting.consentNotice && (
              <InfoField>
                <InfoLabel>Recording Notice</InfoLabel>
                <InfoValue style={{ fontSize: 13 }}>
                  {meeting.consentNotice.delivered === true && (
                    `Shown in the meeting${meeting.consentNotice.sentAt ? ` at ${formatDate(meeting.consentNotice.sentAt)}` : ''}` +
                    (meeting.consentNotice.repostCount > 0
                      ? ` · repeated for late joiners ${meeting.consentNotice.repostCount} time${meeting.consentNotice.repostCount === 1 ? '' : 's'}` +
                        (meeting.consentNotice.lastRepostAt ? ` (last ${formatDate(meeting.consentNotice.lastRepostAt)})` : '')
                      : '')
                  )}
                  {meeting.consentNotice.delivered === false &&
                    'Not shown (Murphy never made it into the call)'}
                  {meeting.consentNotice.delivered == null && 'Configured for this meeting'}
                </InfoValue>
              </InfoField>
            )}
            {((scope === 'team' && meeting.salesPersonName) || (isSharedWithMe && ownerName)) && (
              <InfoField>
                <InfoLabel>Sales Rep</InfoLabel>
                <InfoValue>{ownerName || meeting.salesPersonName}</InfoValue>
              </InfoField>
            )}
            {meeting.assessment?.dealStage && meeting.assessment.dealStage !== 'unknown' && (
              <InfoField>
                <InfoLabel>Deal Stage</InfoLabel>
                <InfoValue style={{ textTransform: 'capitalize' }}>{meeting.assessment.dealStage}</InfoValue>
              </InfoField>
            )}
          </div>
          {(isDelivered || canManageMeeting) && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {canManageMeeting && (
                  <ManageMeetingControls
                    meeting={meeting}
                    myMeetingsStore={myMeetingsStore}
                    companyStore={companyStore}
                    usersStore={usersStore}
                  />
                )}
                {isDelivered && (!isSharedWithMe || canManageMeeting) && <ShareSummaryButton meeting={meeting} />}
                {isDelivered && <DownloadReportButton meeting={meeting} />}
              </div>
              {Array.isArray(meeting.sharedEmailsSent) && meeting.sharedEmailsSent.length > 0 && (
                <div style={{ fontSize: 11, color: '#7a8590', maxWidth: 360, textAlign: 'right', lineHeight: 1.4 }}>
                  Auto-shared with: {meeting.sharedEmailsSent.map(r => r.email).join(', ')}
                </div>
              )}
            </div>
          )}
        </div>

        {participants.length > 0 ? (
          <>
            <InfoLabel style={{ marginTop: 16, display: 'block' }}>Participants</InfoLabel>
            <ParticipantList>
              {participants.map((p, i) => (
                <ParticipantChip key={i} $isMultiplyUser={p.isMultiplyUser}>
                  {p.isMultiplyUser && '★ '}{p.name || p.email}
                </ParticipantChip>
              ))}
            </ParticipantList>
          </>
        ) : (
          <Placeholder>Participants: TBD</Placeholder>
        )}
      </Section>

      {/* Defensive: delivered but missing assessment */}
      {isDelivered && !assessment && (
        <Section>
          <Placeholder>
            Analysis data is missing for this meeting. Contact support if this persists.
          </Placeholder>
        </Section>
      )}

      {/* Analysis sections — only for delivered meetings with an assessment.
          The Murphy review block (Positives / Areas For Improvement / Q&A
          breakdown) is slotted in *between* Problem Alignment and Key Moments
          inside AnalysisSections, so the headline coaching artifact sits next
          to the deal-fit context rather than at the very bottom of the
          analysis stack. Reuses NewAssessmentDetails — the same component
          practice-pitch reviews use — by feeding it a derived view object
          (positivesView/areasForImprovementView are MobX computed views on
          the Assessment model; our endpoint returns plain JSON). */}
      {isDelivered && assessment?.liveMeetingAnalysis && (
        <AnalysisSections
          analysis={assessment.liveMeetingAnalysis}
          grade={assessment.grade}
          sessionType={assessment.sessionType}
          murphyReviewSlot={
            assessment ? (
              <Section>
                <NewAssessmentDetails
                  assessments={[withDerivedViews(assessment)]}
                  canEditLibrary={canEditLibrary}
                  existingQuestions={companyStore?.company?.questionsAndAnswers || []}
                  onSaveToLibrary={canEditLibrary ? onSaveToLibrary : undefined}
                  hideQuestionBreakdown={true}
                />
              </Section>
            ) : null
          }
        />
      )}

      {/* Question Breakdown — live-meeting Q&A is transcript context, not
          training. Renders Q+A by speaker (seller-asked / prospect-asked)
          via LiveMeetingQABreakdown — a dedicated component that does NOT
          share code with the practice-pitch NewAssessmentDetails (which
          keeps its full coaching shape). The upper Murphy review block
          (Positives / Areas) is slotted in higher up via murphyReviewSlot
          and still uses NewAssessmentDetails. */}
      {isDelivered && assessment?.liveMeetingAnalysis && (
        <Section>
          <LiveMeetingQABreakdown
            assessment={assessment}
            canEditLibrary={canEditLibrary}
            existingQuestions={companyStore?.company?.questionsAndAnswers || []}
            onSaveToLibrary={canEditLibrary ? onSaveToLibrary : undefined}
          />
        </Section>
      )}

      {/* Edge case: delivered + assessment exists but liveMeetingAnalysis is
          missing (older assessment, partial analysis). Surface the Murphy
          review block on its own so the user still sees Positives / Areas. */}
      {isDelivered && assessment && !assessment.liveMeetingAnalysis && (
        <Section>
          <NewAssessmentDetails
            assessments={[withDerivedViews(assessment)]}
            canEditLibrary={canEditLibrary}
            existingQuestions={companyStore?.company?.questionsAndAnswers || []}
            onSaveToLibrary={canEditLibrary ? onSaveToLibrary : undefined}
          />
        </Section>
      )}

      {/* Transcript — collapsed by default */}
      {isDelivered && (
        <TranscriptSection
          meeting={meeting}
          myMeetingsStore={myMeetingsStore}
          companyId={companyStore?.company?.id}
        />
      )}
    </CardBody>
  );
});

export default MeetingDetail;
